// src/guides/tokens.js
// Token interpolation for Host Playbook guide copy. Guides never hardcode
// dollars, weekdays, cutoffs or brand names — copy carries {tokens} and this
// module resolves them from live config at render time, so a guide can only
// promise what the brand actually runs (same philosophy as the WelcomeTour's
// launchpadFacts). Pure + browser-free so scripts/guides.test.mjs can run it
// under plain node.

// Explicit .js extension: keeps this module importable by the node test.
import { money } from '../utils/gamificationPerks.js';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// '23:00' → '11PM', '00:00' → '12AM', '19:30' → '7:30PM'. Empty/invalid input
// falls back to the neutral phrase 'cut-off' so copy like "valid before
// {cutoffTime}" still reads as a sentence when a brand has no cutoff set.
export function formatTimeLabel(hhmm, fallback = 'cut-off') {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm ?? '').trim());
  if (!m) return fallback;
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  if (hours > 23 || minutes > 59) return fallback;
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  return minutes ? `${h12}:${String(minutes).padStart(2, '0')}${suffix}` : `${h12}${suffix}`;
}

// The full token table (spec §4.3). eventConfig = EventContext config shape
// (eventDefaults.js), gamificationConfig = normalized useGamificationConfig.
export function buildGuideTokens({ eventConfig, gamificationConfig } = {}) {
  const brand = eventConfig?.brand || {};
  const weekday = Number(eventConfig?.night?.weekday);
  const referral = gamificationConfig?.referral || {};
  const missions = gamificationConfig?.missions || {};
  return {
    brandName: String(brand.name || '').trim(),
    brandShortName: String(brand.shortName || brand.name || '').trim(),
    nightDayName: DAY_NAMES[weekday] || 'event',
    cutoffTime: formatTimeLabel(eventConfig?.rules?.frontDoor?.discountPricing?.cutoffTime),
    referralRecruiterBonus: money(Number(referral.recruiterBonus) || 0),
    referralRecruitBonus: money(Number(referral.recruitBonus) || 0),
    referralActivationPayers: String(Number(referral.activationPayers) || 0),
    cashoutMinimum: money(Number(gamificationConfig?.cashout?.minimum) || 0),
    seasonLengthWeeks: String(Number(missions.seasonLengthWeeks) || 0)
  };
}

// Warn once per unknown token name, not once per render.
const warnedTokens = new Set();

export function resolveGuideText(text, tokens) {
  return String(text ?? '').replace(/\{([a-zA-Z0-9_]+)\}/g, (_, name) => {
    if (tokens && Object.prototype.hasOwnProperty.call(tokens, name)) return tokens[name];
    if (!warnedTokens.has(name)) {
      warnedTokens.add(name);
      console.warn(`[guides] unknown copy token {${name}} — rendering blank`);
    }
    return '';
  });
}
